import { useEffect, useState } from 'react';
import { getCertificates, issueCertificate, getStudents, getStudent } from '../../../api/adminApi';
import Toast from '../../../components/admin/Toast';

const AV_COLORS = ['#E8440A','#F7C91B','#1A7048','#5B6ABF','#D4882A','#8A6FB0'];
function avColor(id) {
  let h = 0;
  for (let i = 0; i < String(id).length; i++) h = String(id).charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CertPreview({ cert, onClose }) {
  const accent = '#D4A017';
  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title">
          Certificate Preview
          <button className="btn btn-o btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{
          background: 'white', border: `2px solid ${accent}`,
          borderRadius: 16, padding: 36, textAlign: 'center', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', inset: 8, border: `1.5px solid ${accent}`, borderRadius: 12, pointerEvents: 'none' }} />
          <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 10, textTransform: 'uppercase', letterSpacing: 2, color: 'var(--muted)', marginBottom: 6 }}>
            Certificate of Completion
          </div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 22, color: accent, marginBottom: 4 }}>
            {cert.progress_percent >= 90 ? 'With Honors' : 'Standard'}
          </div>
          <div style={{ width: 48, height: 2, background: accent, margin: '14px auto', borderRadius: 1 }} />
          <div style={{ fontSize: 11, color: 'var(--subtle)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 4, fontFamily: 'DM Mono,monospace' }}>
            This certifies that
          </div>
          <div style={{ fontFamily: 'Nunito Sans,sans-serif', fontWeight: 800, fontSize: 26, marginBottom: 6 }}>
            {cert.student_name}
          </div>
          <div style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 18 }}>
            has successfully completed <strong>{cert.course_title}</strong>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'DM Mono,monospace' }}>
            Issued {fmtDate(cert.issued_at)}
          </div>
          <div style={{ width: 52, height: 52, margin: '14px auto 0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, background: '#FFF8D4', border: `2px solid ${accent}` }}>
            ✦
          </div>
        </div>
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <span style={{ fontFamily: 'DM Mono,monospace', fontSize: 11, color: 'var(--muted)', letterSpacing: 1 }}>
            {cert.certificate_code}
          </span>
        </div>
      </div>
    </div>
  );
}

function IssueModal({ onClose, onIssued }) {
  const [students,     setStudents]     = useState([]);
  const [selStudent,   setSelStudent]   = useState('');
  const [enrollments,  setEnrollments]  = useState([]);
  const [selEnroll,    setSelEnroll]    = useState('');
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [saving,       setSaving]       = useState(false);

  useEffect(() => {
    getStudents().then(d => setStudents(d.students)).catch(console.error);
  }, []);

  async function onStudentChange(id) {
    setSelStudent(id);
    setSelEnroll('');
    setEnrollments([]);
    if (!id) return;
    setLoadingEnroll(true);
    try {
      const d = await getStudent(id);
      const eligible = (d.enrollments || []).filter(e => !e.certificate_code);
      setEnrollments(eligible);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEnroll(false);
    }
  }

  async function handleIssue(e) {
    e.preventDefault();
    if (!selEnroll) return;
    setSaving(true);
    try {
      await issueCertificate(selEnroll);
      onIssued();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-title">
          ✦ Issue Certificate
          <button className="btn btn-o btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleIssue}>
          <div className="fg">
            <label className="fl">Student</label>
            <select className="li" value={selStudent} onChange={e => onStudentChange(e.target.value)} required>
              <option value="">Select a student…</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.full_name} — {s.email}</option>
              ))}
            </select>
          </div>
          <div className="fg">
            <label className="fl">Course Enrollment</label>
            <select className="li" value={selEnroll} onChange={e => setSelEnroll(e.target.value)} required disabled={!selStudent || loadingEnroll}>
              <option value="">{loadingEnroll ? 'Loading…' : enrollments.length === 0 && selStudent ? 'No eligible enrollments' : 'Select a course…'}</option>
              {enrollments.map(e => (
                <option key={e.enrollment_id} value={e.enrollment_id}>
                  {e.course.emoji} {e.course.title} — {e.progress_percent}% complete
                </option>
              ))}
            </select>
            {selStudent && !loadingEnroll && enrollments.length === 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                This student has no enrollments without a certificate yet.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button type="button" className="btn btn-o" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-p" disabled={saving || !selEnroll}>
              {saving ? 'Issuing…' : '✦ Issue Certificate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CertificateList() {
  const [certs,   setCerts]   = useState([]);
  const [search,  setSearch]  = useState('');
  const [viewing, setViewing] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast,   setToast]   = useState(null);

  function load() {
    setLoading(true);
    getCertificates()
      .then(d => setCerts(d.certificates))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  const filtered = certs.filter(c =>
    c.student_name.toLowerCase().includes(search.toLowerCase()) ||
    c.course_title.toLowerCase().includes(search.toLowerCase()) ||
    (c.course_code && c.course_code.toLowerCase().includes(search.toLowerCase()))
  );

  function onIssued() {
    load();
    setToast({ msg: '✦ Certificate issued successfully!', type: 'success' });
  }

  return (
    <>
      <div className="ph">
        <div>
          <div className="bc">Admin / Certificates</div>
          <div className="pt">Certificates</div>
          <div className="ps">Issue and manage course completion certificates</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search-box">
            <input placeholder="Search certificates…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-p btn-sm" onClick={() => setIssuing(true)}>✦ Issue Certificate</button>
        </div>
      </div>

      <div className="body">
        <div className="card" style={{ padding: 0 }}>
          {loading ? (
            <div className="empty"><div style={{ color: 'var(--muted)' }}>Loading…</div></div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div style={{ fontSize: 44, opacity: 0.5, marginBottom: 12 }}>✦</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                {search ? 'No certificates match your search' : 'No certificates issued yet'}
              </div>
              {!search && (
                <button className="btn btn-p btn-sm" style={{ marginTop: 16 }} onClick={() => setIssuing(true)}>
                  ✦ Issue First Certificate
                </button>
              )}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Template</th>
                    <th>Issued</th>
                    <th style={{ width: 80 }}>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(cert => (
                    <tr key={cert.enrollment_id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                          <div className="av" style={{ background: avColor(cert.student_id), color: 'white', fontSize: 10, flexShrink: 0 }}>
                            {initials(cert.student_name)}
                          </div>
                          <span style={{ fontWeight: 600 }}>{cert.student_name}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>{cert.course_emoji}</span>
                          <div>
                            {cert.course_code && (
                              <span style={{ fontFamily: 'DM Mono,monospace', fontSize: 10, color: 'var(--muted)', marginRight: 5 }}>
                                {cert.course_code}
                              </span>
                            )}
                            {cert.course_title}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`b ${cert.progress_percent >= 90 ? 'b-y' : 'b-gr'}`}>
                          {cert.progress_percent >= 90 ? 'honors' : 'standard'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--muted)', fontFamily: 'DM Mono,monospace', fontSize: 12 }}>
                        {fmtDate(cert.issued_at)}
                      </td>
                      <td>
                        <button className="btn btn-o btn-sm" onClick={() => setViewing(cert)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {issuing && (
        <IssueModal
          onClose={() => setIssuing(false)}
          onIssued={onIssued}
        />
      )}

      {viewing && <CertPreview cert={viewing} onClose={() => setViewing(null)} />}

      {toast && (
        <Toast
          message={typeof toast === 'string' ? toast : toast.msg}
          type={typeof toast === 'string' ? 'success' : toast.type}
          onDone={() => setToast(null)}
        />
      )}
    </>
  );
}
